import { jsx } from 'react/jsx-runtime';
import { customInterpolation } from '@lemonade-hq/cdk';
import { DEFAULT_LOCALE, DEFAULT_REGION, DEFAULT_LANGUAGE, ACTIVE_REGIONS, getActiveLocales, DEFAULT_LANGUAGE_BY_REGION, getLocale, EN_US_LOCALE, EN_DE_LOCALE, EN_FR_LOCALE, EN_NL_LOCALE, EN_GB_LOCALE, DE_DE_LOCALE, NL_NL_LOCALE, FR_FR_LOCALE } from '@lemonade-hq/lemonation';
import { typedKeys, getEnvVar } from '@lemonade-hq/ts-helpers';
import * as Sentry from '@sentry/browser';
import merge from 'lodash/merge';
import React, { createContext, useState, useMemo, useRef, useEffect, useCallback, useContext } from 'react';
import { isDev } from '../helpers/infra.esm.js';
import deDeTranslation from '../translations/de-DE.esm.js';
import enDeTranslation from '../translations/en-DE.esm.js';
import enFrTranslation from '../translations/en-FR.esm.js';
import enGBTranslation from '../translations/en-GB.esm.js';
import enNlTranslation from '../translations/en-NL.esm.js';
import enUsTranslation from '../translations/en-US.esm.js';
import frFrTranslation from '../translations/fr-FR.esm.js';
import nlNlTranslation from '../translations/nl-NL.esm.js';

/* eslint-disable no-console */ function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var _obj;
var TRANSLATIONS_FALLBACKS = (_obj = {}, _define_property(_obj, EN_DE_LOCALE, EN_US_LOCALE), _define_property(_obj, EN_FR_LOCALE, EN_US_LOCALE), _define_property(_obj, EN_NL_LOCALE, EN_US_LOCALE), _define_property(_obj, EN_GB_LOCALE, EN_US_LOCALE), _obj);
var _obj1;
var NUIZA_TRANSLATIONS = (_obj1 = {}, _define_property(_obj1, EN_US_LOCALE, enUsTranslation), _define_property(_obj1, DE_DE_LOCALE, deDeTranslation), _define_property(_obj1, EN_DE_LOCALE, enDeTranslation), _define_property(_obj1, NL_NL_LOCALE, nlNlTranslation), _define_property(_obj1, EN_NL_LOCALE, enNlTranslation), _define_property(_obj1, FR_FR_LOCALE, frFrTranslation), _define_property(_obj1, EN_FR_LOCALE, enFrTranslation), _define_property(_obj1, EN_GB_LOCALE, enGBTranslation), _obj1);
var I18N_DEBUG_PARAM = "i18n_debug";
var isDebugMode = function() {
    // debug mode should only work in dev env
    if (!isDev()) {
        return false;
    }
    // triggered by an environment variable, useful for unit testing
    var isTranslationDebugMode = Boolean(getEnvVar("I18N_DEBUG"));
    if (isTranslationDebugMode) {
        return true;
    }
    if (typeof window !== "object") {
        return false;
    }
    return new URL(window.location.href).searchParams.has(I18N_DEBUG_PARAM);
};
/* eslint-disable @typescript-eslint/no-explicit-any */ function memoize(fn) {
    var transformer = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : function(val) {
        return val;
    };
    var cache = {};
    return function() {
        for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
            args[_key] = arguments[_key];
        }
        var hash = transformer.apply(void 0, _to_consumable_array(args)); // eslint-disable-line @typescript-eslint/no-unsafe-argument
        if (cache[hash] == null) {
            cache[hash] = fn.apply(void 0, _to_consumable_array(args)); // eslint-disable-line @typescript-eslint/no-unsafe-argument
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return cache[hash];
    };
}
function interpolatePlainString(str) {
    var locals = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    var OPENING = "{";
    var CLOSING = "}";
    var finalString = "";
    var bracketCounter = 0;
    var keyName = "";
    for(var i = 0; i < str.length; i += 1){
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        var currentChar = str[i];
        if (typeof currentChar !== "object") {
            if (bracketCounter === 2 && ![
                OPENING,
                CLOSING
            ].includes(currentChar)) {
                keyName += currentChar;
            } else if (currentChar === OPENING && bracketCounter < 2) {
                bracketCounter += 1;
            } else if (currentChar === CLOSING && bracketCounter > 0) {
                bracketCounter -= 1;
                if (bracketCounter === 0) {
                    var localValue = locals[keyName];
                    if (localValue == null) {
                        reportMissingLocal(str, locals);
                    }
                    finalString += localValue !== null && localValue !== void 0 ? localValue : "";
                    keyName = "";
                }
            } else {
                finalString += currentChar;
            }
        }
    }
    return finalString;
}
var memoizedInterpolate = memoize(interpolatePlainString, function(str, locals) {
    var noJsx = Object.keys(locals).reduce(function(acc, key) {
        var val = locals[key];
        if (!/*#__PURE__*/ React.isValidElement(val)) {
            acc[key] = locals[key];
        }
        return acc;
    }, {});
    return str + JSON.stringify(noJsx);
});
// HELPERS: ERRORS *****************************************
var MISSING_TRANSLATIONS_ALREADY_REPORTED_THIS_SESSION = {};
var MISSING_INTERPOLATION_ALREADY_REPORTED_THIS_SESSION = {};
function reportMissingTranslation(path, locale) {
    var key = "".concat(locale, "-").concat(path);
    if (MISSING_TRANSLATIONS_ALREADY_REPORTED_THIS_SESSION[key]) return; // don't report missing content more than once
    MISSING_TRANSLATIONS_ALREADY_REPORTED_THIS_SESSION[key] = true;
    Sentry.captureMessage("Missing i18n in client-side for ".concat(locale, ": ").concat(path));
}
function reportMissingLocal(str, locals) {
    if (MISSING_INTERPOLATION_ALREADY_REPORTED_THIS_SESSION[str]) return; // don't report missing content more than once
    MISSING_INTERPOLATION_ALREADY_REPORTED_THIS_SESSION[str] = true;
    Sentry.captureMessage("Missing i18n interpolation in client-side for string ".concat(str, ", with locals ").concat(JSON.stringify(locals)));
}
// HELPERS: TRANSLATION ************************************
function getStringFromOriginalLocale(path, locale, translations) {
    var translationsForLocale = translations[locale];
    var match = translationsForLocale === null || translationsForLocale === void 0 ? void 0 : translationsForLocale[path];
    return match != null && match.length > 0 ? match : undefined;
}
function getStringFromFallback(path, locale, translations, fallbacks) {
    var _translations_fallbackLocale;
    var fallbackLocale = fallbacks[locale];
    return fallbackLocale != null ? (_translations_fallbackLocale = translations[fallbackLocale]) === null || _translations_fallbackLocale === void 0 ? void 0 : _translations_fallbackLocale[path] : undefined;
}
function translateString(locale, translations, fallbacks) {
    return function(path) {
        var locals = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        if (isDebugMode()) {
            return path;
        }
        var _getStringFromOriginalLocale;
        var match = (_getStringFromOriginalLocale = getStringFromOriginalLocale(path, locale, translations)) !== null && _getStringFromOriginalLocale !== void 0 ? _getStringFromOriginalLocale : getStringFromFallback(path, locale, translations, fallbacks);
        if (match == null || match.length === 0) {
            reportMissingTranslation(path, locale);
            return isDev() ? "\uD83D\uDEAB ".concat(path, " \uD83D\uDEAB") : "";
        }
        return memoizedInterpolate(match, locals);
    };
}
function translateWithJsx(locale, translations, fallbacks) {
    return function(path) {
        var locals = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}, config = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
        if (isDebugMode()) {
            return path;
        }
        var interpolations = config.interpolations;
        var translatedStr = translateString(locale, translations, fallbacks)(path, locals);
        if (interpolations != null && Object.keys(interpolations).length > 0) {
            return customInterpolation(translatedStr, interpolations);
        }
        return translatedStr;
    };
}
function mergeAndVerifyExtendedTranslations(originalTranslations, originalExtensions, newExtensions) {
    return typedKeys(newExtensions).reduce(function(acc, locale) {
        var originalExtensionsForLocale = originalExtensions[locale];
        var newExtensionsForLocale = newExtensions[locale]; // eslint-disable-line @typescript-eslint/no-non-null-assertion
        if (originalExtensionsForLocale == null) {
            acc[locale] = newExtensionsForLocale;
        } else {
            acc[locale] = _object_spread({}, originalExtensionsForLocale, newExtensionsForLocale);
        }
        var originalTranslationsLocale = originalTranslations[locale];
        var everythingMerged = _object_spread({}, originalTranslationsLocale, originalExtensionsForLocale, newExtensionsForLocale);
        if (Object.keys(everythingMerged).length !== Object.keys(originalTranslationsLocale !== null && originalTranslationsLocale !== void 0 ? originalTranslationsLocale : {}).length + Object.keys(originalExtensionsForLocale !== null && originalExtensionsForLocale !== void 0 ? originalExtensionsForLocale : {}).length + Object.keys(newExtensionsForLocale).length) {
            throw new Error("Extending translations keys are overriding existing ones in ".concat(locale));
        }
        return acc;
    }, {});
}
// HELPERS: CONTEXT ************************************
/* istanbul ignore next */ var DEFAULT_I18N_CONTEXT_VALUE = {
    locale: DEFAULT_LOCALE,
    region: DEFAULT_REGION,
    lang: DEFAULT_LANGUAGE,
    activeRegions: ACTIVE_REGIONS,
    activeLocales: getActiveLocales(ACTIVE_REGIONS),
    t: function() {
        return null;
    },
    s: function() {
        return "";
    },
    extendTranslations: function() {
        return null;
    }
};
/* istanbul ignore next */ var I18nContext = /*#__PURE__*/ createContext(DEFAULT_I18N_CONTEXT_VALUE);
/* istanbul ignore next */ var CommonI18nContext = /*#__PURE__*/ createContext(DEFAULT_I18N_CONTEXT_VALUE);
// HELPERS: PROVIDER ************************************
var I18nProvider = function(param) {
    var _param_region = param.region, region = _param_region === void 0 ? DEFAULT_REGION : _param_region, _param_lang = param.lang, lang = _param_lang === void 0 ? DEFAULT_LANGUAGE : _param_lang, _param_activeRegions = param.activeRegions, activeRegions = _param_activeRegions === void 0 ? ACTIVE_REGIONS : _param_activeRegions, _param_translations = param.translations, translations = _param_translations === void 0 ? {} : _param_translations, _param_fallbacks = param.fallbacks, fallbacks = _param_fallbacks === void 0 ? TRANSLATIONS_FALLBACKS : _param_fallbacks, _param_includeCommonI18nContext = param.includeCommonI18nContext, includeCommonI18nContext = _param_includeCommonI18nContext === void 0 ? true : _param_includeCommonI18nContext, children = param.children;
    var _useState = _sliced_to_array(useState({}), 2), extendedTranslations = _useState[0], setExtendedTranslations = _useState[1];
    var isActiveRegion = activeRegions.includes(region);
    var activeLocales = useMemo(function() {
        return getActiveLocales(activeRegions);
    }, [
        activeRegions
    ]);
    var translationsRef = useRef(translations);
    var resolvedRegion = isActiveRegion ? region : DEFAULT_REGION;
    var resolvedLanguage = isActiveRegion ? lang : DEFAULT_LANGUAGE_BY_REGION[DEFAULT_REGION];
    var locale = getLocale(resolvedRegion, resolvedLanguage);
    useEffect(function() {
        translationsRef.current = translations;
    }, [
        translations
    ]);
    var extendTranslations = useCallback(function(extensions) {
        setExtendedTranslations(function(state) {
            return mergeAndVerifyExtendedTranslations(translationsRef.current, state, extensions);
        });
    }, []);
    var generalValue = useMemo(function() {
        return {
            locale: locale,
            region: resolvedRegion,
            lang: lang,
            activeRegions: activeRegions,
            activeLocales: activeLocales,
            t: translateWithJsx(locale, merge({}, translations, extendedTranslations), fallbacks),
            s: translateString(locale, merge({}, translations, extendedTranslations), fallbacks),
            extendTranslations: extendTranslations
        };
    }, [
        locale,
        resolvedRegion,
        lang,
        activeRegions,
        activeLocales,
        translations,
        extendedTranslations,
        fallbacks,
        extendTranslations
    ]);
    var commonValue = useMemo(function() {
        return _object_spread_props(_object_spread({}, generalValue), {
            t: translateWithJsx(locale, merge({}, NUIZA_TRANSLATIONS, extendedTranslations), fallbacks),
            s: translateString(locale, merge({}, NUIZA_TRANSLATIONS, extendedTranslations), fallbacks)
        });
    }, [
        generalValue,
        locale,
        extendedTranslations,
        fallbacks
    ]);
    return /*#__PURE__*/ jsx(I18nContext.Provider, {
        value: generalValue,
        children: includeCommonI18nContext ? /*#__PURE__*/ jsx(CommonI18nContext.Provider, {
            value: commonValue,
            children: children
        }) : children
    });
};
function useI18nContext() {
    return useContext(I18nContext);
}
function useCommonI18nContext() {
    return useContext(CommonI18nContext);
}

export { CommonI18nContext, DEFAULT_I18N_CONTEXT_VALUE, I18nContext, I18nProvider, useCommonI18nContext, useI18nContext };
